import { formatCollectionVersionRef, parseCollectionVersionRef, parseContentAddress, type CollectionVersionRef, type DocumentVersionRef } from "../../shared/references/contentReferences";
import type { ApiSchema } from "../apiSchema";
import { requestJson, withQuery } from "../http/httpClient";
import { JsonContractError, readArray, readBoolean, readInteger, readIntegerRecord, readNullableString, readObject, readOneOf, readString, type JsonObject } from "../http/jsonDecoding";
import { readCollectionVersionRef, readDocumentVersionRef, readStringList } from "./contentDecoders";
import { ITEM_KINDS, type ItemKind } from "./reviewDecoders";

/**
 * DMW's one search (`docs/components/content.md` D-16, "As built: one search"): the project's
 * corpora in one query, and the library's items in the same syntax (F20, F31, F38). Matches
 * in a snippet are marked with `[` `]`.
 */

/** The engine this installation runs and what queries may use beyond words, phrases, AND, OR. */
export interface SearchEngine {
  readonly engine: ApiSchema<"SearchEngineBody">["engine"];
  readonly features: readonly string[];
}

/** The engine feature for `word~1` and `word~2`. */
export const FUZZY_FEATURE = "fuzzy";

/** Which corpora a search covers. Empty lists search all of the project's corpora. */
export interface CorpusSearchScope {
  /** Corpus IDs, each searched at its latest version. */
  readonly corpusIds: readonly string[];
  /** Exact corpus versions. */
  readonly versions: readonly CollectionVersionRef[];
}

export const ALL_CORPORA: CorpusSearchScope = { corpusIds: [], versions: [] };

/**
 * Which documents a search or a document list keeps by the data attached to them (F88, F91;
 * Simeon's regest search): every kind, label and model term named, or nothing attached (v1's
 * raw regests). Kinds are classes of the DMW process ontology, labels `<label list item ID>#<key>`,
 * terms IRIs of the project's model.
 */
export type AttachedFilter =
  | {
      readonly kind: "with";
      readonly kinds: readonly string[];
      readonly labels: readonly string[];
      readonly terms: readonly string[];
      readonly acceptedOnly: boolean;
    }
  | { readonly kind: "nothing"; readonly acceptedOnly: boolean };

/** A filter that names what the documents must have attached. */
export type AttachedWithFilter = Extract<AttachedFilter, { readonly kind: "with" }>;

/** Keeps every document. */
export const ANY_ATTACHED: AttachedWithFilter = { kind: "with", kinds: [], labels: [], terms: [], acceptedOnly: false };

/** The filter as the search and the document lists take it in their query. */
export function attachedFilterQuery(filter: AttachedFilter): Readonly<Record<string, readonly string[] | string | null>> {
  const acceptedOnly = filter.acceptedOnly ? "true" : null;
  if (filter.kind === "nothing") {
    return { attached: [NOTHING_ATTACHED], label: [], term: [], accepted_only: acceptedOnly };
  }
  return { attached: filter.kinds, label: filter.labels, term: filter.terms, accepted_only: acceptedOnly };
}

/** The server's `attached=none` (`attached_bodies.py`). */
const NOTHING_ATTACHED = "none";

/** One kind of data attached to a document, in its state; both are DMW process ontology terms. */
export interface AttachedForm {
  readonly kind: string;
  /** `dmwp:Accepted` or `dmwp:Proposed`. */
  readonly state: string;
}

/** What the filters can name in a project, each with how many documents have it. */
export interface AttachedChoices {
  /** Documents with each data class attached, the most developed class first. */
  readonly kinds: readonly { readonly kind: string; readonly documents: number }[];
  readonly labels: readonly AttachedLabelChoice[];
  /** Documents whose attached statements say a text, for a search in them (F141). */
  readonly statedDocuments: number;
  readonly terms: readonly { readonly term: string; readonly documents: number }[];
  /** The model version to name the terms by (PC-5): the project's latest; `null` without project content. */
  readonly termsNamedAt: CollectionVersionRef | null;
}

export interface AttachedLabelChoice {
  /** As the filter names it: `<label list item ID>#<key>`. */
  readonly label: string;
  /** A version of its label list, to read the label's name from. */
  readonly labelList: string;
  readonly key: string;
  readonly documents: number;
  /** Documents with it per kind of segment it lies in, for a search limited to one (F141). */
  readonly segments: Readonly<Record<string, number>>;
}

/** A document's attached data, the most developed form first; empty for a raw document. */
export function readAttachedForms(owner: JsonObject): readonly AttachedForm[] {
  return readArray(owner["attached"], "attached data", (value) => {
    const form = readObject(value, "attached form");
    return { kind: readString(form, "kind"), state: readString(form, "state") };
  });
}

export function readAttachedChoices(projectId: string, signal?: AbortSignal): Promise<AttachedChoices> {
  return requestJson({
    method: "GET",
    path: `/projects/${encodeURIComponent(projectId)}/attached-data`,
    signal,
    decodeResponse: (body) => {
      const choices = readObject(body, "attached-data choices");
      return {
        kinds: readArray(choices["kinds"], "kinds", (value) => {
          const kind = readObject(value, "kind");
          return { kind: readString(kind, "kind"), documents: readInteger(kind, "documents") };
        }),
        labels: readArray(choices["labels"], "labels", (value) => {
          const label = readObject(value, "label");
          return {
            label: readString(label, "label"),
            labelList: readString(label, "label_list"),
            key: readString(label, "key"),
            documents: readInteger(label, "documents"),
            segments: readIntegerRecord(label, "segments"),
          };
        }),
        statedDocuments: readInteger(choices, "stated_documents"),
        terms: readArray(choices["terms"], "terms", (value) => {
          const term = readObject(value, "term");
          return { term: readString(term, "term"), documents: readInteger(term, "documents") };
        }),
        termsNamedAt: readNullableCorpusVersion(readNullableString(choices, "terms_named_at")),
      };
    },
  });
}

/**
 * Where a search matches its words (F141): the documents' text, in one kind of segment or the
 * whole document, or what the statements attached to them say (`search_in=statements`).
 */
export type SearchIn =
  | { readonly kind: "text"; /** `null`: the whole document. */ readonly segmentType: string | null }
  | { readonly kind: "statements" };

export const WHOLE_TEXT: SearchIn = { kind: "text", segmentType: null };

export interface CorpusSearchRequest {
  readonly query: string;
  readonly scope: CorpusSearchScope;
  readonly searchIn: SearchIn;
  readonly attached: AttachedFilter;
  readonly limit: number;
}

/** One document found, with the segment where it matches most, if the engine names one. */
export interface CorpusSearchHit {
  readonly corpusVersion: CollectionVersionRef;
  readonly document: DocumentVersionRef;
  readonly segmentPath: string | null;
  readonly documentKey: string;
  /** The key as its corpus's key forms write it short, such as `3/57` (F136); `null` if none does. */
  readonly shortKey: string | null;
  readonly snippet: string;
  readonly attached: readonly AttachedForm[];
  /** The query is this document's key, in full or in a short form its corpus declares; such hits come first. */
  readonly byKey: boolean;
}

export interface CorpusSearchResult {
  readonly corpusVersions: readonly CollectionVersionRef[];
  readonly engine: SearchEngine;
  readonly hits: readonly CorpusSearchHit[];
}

export interface LibrarySearchRequest {
  /** Blank lists the items of the kinds and content types named, by name. */
  readonly query: string;
  readonly kinds: readonly ItemKind[];
  /** Artifact content types; empty for every type. */
  readonly contentTypes: readonly string[];
  readonly limit: number;
}

/** A library item at the project's latest `main`. */
export interface LibrarySearchItem {
  readonly kind: ItemKind;
  readonly reference: string;
  readonly name: string;
  /** An artifact's content type, such as `markdown`; `null` for other kinds. */
  readonly contentType: string | null;
  /** The body around its first match; `null` for a blank query or a match in the name only. */
  readonly snippet: string | null;
}

/** The library search answers with at most this many items. */
export const LIBRARY_SEARCH_LIMIT = 100;

export function readSearchEngine(signal?: AbortSignal): Promise<SearchEngine> {
  return requestJson({
    method: "GET",
    path: "/content/search-engine",
    signal,
    decodeResponse: (body) => decodeEngine(readObject(body, "search engine")),
  });
}

export function searchCorpora(projectId: string, request: CorpusSearchRequest, signal?: AbortSignal): Promise<CorpusSearchResult> {
  return requestJson({
    method: "GET",
    path: withQuery(`/projects/${encodeURIComponent(projectId)}/search`, {
      query: request.query,
      corpus: request.scope.corpusIds,
      version: request.scope.versions.map(formatCollectionVersionRef),
      segment_type: request.searchIn.kind === "text" ? request.searchIn.segmentType : null,
      search_in: request.searchIn.kind === "text" ? null : request.searchIn.kind,
      limit: request.limit,
      ...attachedFilterQuery(request.attached),
    }),
    signal,
    decodeResponse: (body) => {
      const result = readObject(body, "search result");
      return {
        corpusVersions: readStringList(result, "corpus_versions").map(decodeCorpusVersion),
        engine: decodeEngine(readObject(result["engine"], "search engine")),
        hits: readArray(result["hits"], "search hits", (value) => decodeHit(readObject(value, "search hit"))),
      };
    },
  });
}

export function searchLibrary(projectId: string, request: LibrarySearchRequest, signal?: AbortSignal): Promise<readonly LibrarySearchItem[]> {
  return requestJson({
    method: "GET",
    path: withQuery(`/projects/${encodeURIComponent(projectId)}/content/library/search`, {
      query: request.query,
      kinds: request.kinds,
      schemas: request.contentTypes,
      limit: request.limit,
    }),
    signal,
    decodeResponse: (body) => readArray(readObject(body, "library search")["items"], "library items", (value) => {
      const item = readObject(value, "library item");
      return {
        kind: readOneOf(item, "kind", ITEM_KINDS),
        reference: readString(item, "reference"),
        name: readString(item, "name"),
        contentType: readNullableString(item, "schema"),
        snippet: readNullableString(item, "snippet"),
      };
    }),
  });
}

function decodeEngine(engine: JsonObject): SearchEngine {
  return { engine: readString(engine, "engine"), features: readStringList(engine, "features") };
}

function readNullableCorpusVersion(text: string | null): CollectionVersionRef | null {
  return text === null ? null : decodeCorpusVersion(text);
}

function decodeCorpusVersion(text: string): CollectionVersionRef {
  const version = parseCollectionVersionRef(text);
  if (version === null) throw new JsonContractError("Expected a corpus version reference.");
  return version;
}

/** The path of a `<item id>@<blob id>#<segment path>` reference. */
function decodeSegmentPath(segment: string | null): string | null {
  if (segment === null) return null;
  const segmentPath = parseContentAddress(segment)?.segmentPath ?? null;
  if (segmentPath === null) throw new JsonContractError('Expected "segment" to be a segment reference.');
  return segmentPath;
}

function decodeHit(hit: JsonObject): CorpusSearchHit {
  return {
    corpusVersion: readCollectionVersionRef(hit, "corpus_version"),
    document: readDocumentVersionRef(hit, "document"),
    segmentPath: decodeSegmentPath(readNullableString(hit, "segment")),
    documentKey: readString(hit, "document_key"),
    shortKey: readNullableString(hit, "short_key"),
    snippet: readString(hit, "snippet"),
    attached: readAttachedForms(hit),
    byKey: readBoolean(hit, "by_key"),
  };
}
